import React, { useState, useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Badge } from 'primereact/badge';

import { getAuthHeader } from '@/utils';
import { BASE_URL } from '@core/constants';
import {
  useActiveTranscriptionTasks,
  cancelTranscriptionTask,
} from '@modules/imaging/hooks/useTranscriptionTasks';
import '../styles/ActiveTasksBadge.css';

// Componente auxiliar para reloj en vivo (fuera del componente principal)
const TaskElapsed = ({ startedAt }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt) return;
    const start = new Date(startedAt).getTime();
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return (
    <span
      style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary-blue, #4596D3)' }}
    >
      {mins}:{String(secs).padStart(2, '0')}
    </span>
  );
};

// Labels de estado legibles
const STATUS_LABELS = {
  pending: 'en cola',
  processing: 'procesando',
  retry: 'reintentando',
};

// ==========================================================================
// LEGACY (pre-SSE, deshabilitado 2026-09-01): polling propio cada 2s +
// lectura/escritura directa de localStorage.activeTranscriptionTasks.
// Reemplazado por useActiveTranscriptionTasks() (cache de TanStack Query,
// alimentado por SSE vía useTranscriptionTasksSync.js), que es ahora la
// única fuente de verdad -- este componente y GenerateReport.jsx/
// ReportEditionStep.jsx antes escribían la misma clave de localStorage sin
// coordinarse entre sí (condición de carrera). Función NO exportada ni
// referenciada -- código muerto en la práctica, se deja intacto (sin
// comentario de bloque, porque las {/* ... */} de JSX de más abajo cortan
// un /* */ que las envuelva) por si hay que revertir rápido.
// ==========================================================================
const ActiveTasksBadge_LEGACY = () => {
  const [activeTasks, setActiveTasks] = useState([]);

  // Estados para el panel desplegable
  const [isOpen, setIsOpen] = useState(false);
  const [cancellingIds, setCancellingIds] = useState([]);
  const containerRef = useRef(null);

  // dedupeTasks sin cambios
  const dedupeTasks = tasks => {
    const source = Array.isArray(tasks) ? tasks : [];
    const byTaskId = new Map();

    source.forEach(task => {
      if (task?.taskId) {
        byTaskId.set(task.taskId, task);
      }
    });

    return Array.from(byTaskId.values());
  };

  useEffect(() => {
    // Cargar tareas iniciales
    const loadTasks = () => {
      const savedTasks = JSON.parse(localStorage.getItem('activeTranscriptionTasks') || '[]');
      const deduped = dedupeTasks(savedTasks);
      setActiveTasks(deduped);
      localStorage.setItem('activeTranscriptionTasks', JSON.stringify(deduped));
    };

    loadTasks();

    // Polling estable cada 2 segundos (SIN dependencias problemáticas)
    const interval = setInterval(() => {
      const currentTasks = JSON.parse(localStorage.getItem('activeTranscriptionTasks') || '[]');
      const dedupedCurrentTasks = dedupeTasks(currentTasks);
      if (dedupedCurrentTasks.length !== currentTasks.length) {
        localStorage.setItem('activeTranscriptionTasks', JSON.stringify(dedupedCurrentTasks));
      }

      // Si hay tareas, verificar su estado
      if (dedupedCurrentTasks.length > 0) {
        checkTasksStatus();
      } else {
        // Si localStorage está vacío, limpiar estado local
        setActiveTasks([]);
      }
    }, 2000); // 2 segundos para actualizaciones rápidas

    // Listeners para sincronización entre pestañas y cancelaciones
    const handleStorageChange = e => {
      if (e.key === 'activeTranscriptionTasks') loadTasks();
    };
    const handleTaskCancelled = () => loadTasks();

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('transcriptionTaskCancelled', handleTaskCancelled);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('transcriptionTaskCancelled', handleTaskCancelled);
    };
  }, []); // 👈 DEPENDENCIA VACÍA: solo se ejecuta al montar/desmontar

  const checkTasksStatus = async () => {
    try {
      const savedTasks = JSON.parse(localStorage.getItem('activeTranscriptionTasks') || '[]');
      const dedupedSavedTasks = dedupeTasks(savedTasks);

      if (dedupedSavedTasks.length === 0) {
        setActiveTasks([]);
        return;
      }

      const stillActive = [];

      for (const task of dedupedSavedTasks) {
        try {
          const response = await fetch(
            `${BASE_URL}insurance/transcribe/task-status/${task.taskId}/`,
            { headers: getAuthHeader() }
          );

          console.log(`Verificando estado de tarea ${task.taskId} - HTTP ${response}`);
          if (response.ok) {
            const statusData = await response.json();

            // Mantener solo tareas activas
            if (['pending', 'processing', 'retry'].includes(statusData.status)) {
              stillActive.push({
                ...task,
                status: statusData.status,
                message: statusData.message,
                progress: statusData.progress || 0,
              });
            }
          }
        } catch (error) {
          // En caso de error temporal, mantener la tarea
          stillActive.push(task);
        }
      }

      setActiveTasks(stillActive);
      localStorage.setItem('activeTranscriptionTasks', JSON.stringify(stillActive));
    } catch (error) {
      console.error('Error verificando estado de tareas:', error);
    }
  };

  // Cerrar panel al hacer clic fuera
  useEffect(() => {
    const onClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  //  Auto-cerrar panel si no quedan tareas
  useEffect(() => {
    if (activeTasks.length === 0 && isOpen) {
      setIsOpen(false);
    }
  }, [activeTasks.length, isOpen]);

  // Cancelar tarea individual
  const handleCancelTask = async task => {
    if (cancellingIds.includes(task.taskId)) return;
    setCancellingIds(prev => [...prev, task.taskId]);

    let backendMessage = 'Tarea cancelada.';
    try {
      const response = await fetch(`${BASE_URL}insurance/transcribe/revoke-task/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ task_id: task.taskId }),
      });
      if (response.ok) {
        const data = await response.json();
        if (!data.revoked && data.message) {
          backendMessage = data.message;
        }
      }
    } catch (err) {
      console.warn('[ActiveTasksBadge] No se pudo revocar en backend:', err);
    }

    try {
      const savedTasks = JSON.parse(localStorage.getItem('activeTranscriptionTasks') || '[]');
      const updated = Array.isArray(savedTasks)
        ? savedTasks.filter(t => t.taskId !== task.taskId)
        : [];
      localStorage.setItem('activeTranscriptionTasks', JSON.stringify(updated));
      setActiveTasks(updated);

      window.dispatchEvent(
        new CustomEvent('transcriptionTaskCancelled', {
          detail: { taskId: task.taskId, source: 'badge' },
        })
      );
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: 'activeTranscriptionTasks',
          newValue: JSON.stringify(updated),
        })
      );
    } catch (err) {
      console.warn('[ActiveTasksBadge] Error limpiando localStorage:', err);
    }

    setCancellingIds(prev => prev.filter(id => id !== task.taskId));
    console.log(`[ActiveTasksBadge] ${backendMessage}`);
  };

  if (activeTasks.length === 0) return null;

  return (
    <div className="active-tasks-badge-container" ref={containerRef}>
      {/* badge con clases CSS originales + clic y chevron agregados */}
      <div
        className="active-tasks-badge"
        onClick={e => {
          e.stopPropagation();
          setIsOpen(o => !o);
        }}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title="Ver tareas en proceso"
      >
        <i className="pi pi-hourglass" />
        <Badge value={activeTasks.length} severity="warning" />
        <span className="badge-description">Procesando...</span>
        {/* Chevron indicador */}
        <i
          className={`pi ${isOpen ? 'pi-chevron-down' : 'pi-chevron-up'}`}
          style={{ marginLeft: 6, fontSize: '0.75rem' }}
        />
      </div>

      {/* Panel desplegable (solo aparece cuando isOpen=true) */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            right: 0,
            width: '300px',
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            zIndex: 9999,
          }}
        >
          {/* Header con colores corporativos */}
          <div
            style={{
              padding: '10px 14px',
              background: 'linear-gradient(135deg, var(--primary-blue, #4596D3) 0%, #0BC4D9 100%)',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <i className="pi pi-hourglass" />
            Transcripciones en proceso
          </div>

          {/* Lista de tareas */}
          {activeTasks.map(task => (
            <div
              key={task.taskId}
              style={{
                padding: '10px 14px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: '#1e293b',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {task.studyName || `Estudio #${task.studyId}`}
                </div>
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: '#64748b',
                    display: 'flex',
                    gap: '6px',
                    alignItems: 'center',
                  }}
                >
                  <TaskElapsed startedAt={task.startedAt} />
                  <span>· {STATUS_LABELS[task.status] || 'procesando'}</span>
                </div>
              </div>

              {/* Botón cancelar */}
              <button
                type="button"
                onClick={() => handleCancelTask(task)}
                disabled={cancellingIds.includes(task.taskId)}
                title="Cancelar esta transcripción"
                style={{
                  background: 'transparent',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#dc2626',
                  padding: '5px 8px',
                  cursor: cancellingIds.includes(task.taskId) ? 'wait' : 'pointer',
                  flexShrink: 0,
                }}
              >
                {cancellingIds.includes(task.taskId) ? (
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.8rem' }} />
                ) : (
                  <i className="pi pi-trash" style={{ fontSize: '0.8rem' }} />
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
// ==========================================================================

const ActiveTasksBadge = () => {
  const queryClient = useQueryClient();
  const allTasks = useActiveTranscriptionTasks();
  const activeTasks = allTasks.filter(t => ['pending', 'processing', 'retry'].includes(t?.status));

  const [isOpen, setIsOpen] = useState(false);
  const [cancellingIds, setCancellingIds] = useState([]);
  const containerRef = useRef(null);

  // Cerrar panel al hacer clic fuera
  useEffect(() => {
    const onClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Auto-cerrar panel si no quedan tareas
  useEffect(() => {
    if (activeTasks.length === 0 && isOpen) {
      setIsOpen(false);
    }
  }, [activeTasks.length, isOpen]);

  // Cancelar tarea individual: única implementación (cancelTranscriptionTask),
  // compartida con ReportEditionStep.jsx en vez de manipular localStorage/eventos a mano.
  const handleCancelTask = async task => {
    if (cancellingIds.includes(task.taskId)) return;
    setCancellingIds(prev => [...prev, task.taskId]);
    try {
      await cancelTranscriptionTask(queryClient, task.taskId);
    } finally {
      setCancellingIds(prev => prev.filter(id => id !== task.taskId));
    }
  };

  if (activeTasks.length === 0) return null;

  return (
    <div className="active-tasks-badge-container" ref={containerRef}>
      {/* badge con clases CSS originales + clic y chevron agregados */}
      <div
        className="active-tasks-badge"
        onClick={e => {
          e.stopPropagation();
          setIsOpen(o => !o);
        }}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title="Ver tareas en proceso"
      >
        <i className="pi pi-hourglass" />
        <Badge value={activeTasks.length} severity="warning" />
        <span className="badge-description">Procesando...</span>
        {/* Chevron indicador */}
        <i
          className={`pi ${isOpen ? 'pi-chevron-down' : 'pi-chevron-up'}`}
          style={{ marginLeft: 6, fontSize: '0.75rem' }}
        />
      </div>

      {/* Panel desplegable (solo aparece cuando isOpen=true) */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            right: 0,
            width: '300px',
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            zIndex: 9999,
          }}
        >
          {/* Header con colores corporativos */}
          <div
            style={{
              padding: '10px 14px',
              background: 'linear-gradient(135deg, var(--primary-blue, #4596D3) 0%, #0BC4D9 100%)',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <i className="pi pi-hourglass" />
            Transcripciones en proceso
          </div>

          {/* Lista de tareas */}
          {activeTasks.map(task => (
            <div
              key={task.taskId}
              style={{
                padding: '10px 14px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: '#1e293b',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {task.studyName || `Estudio #${task.studyId}`}
                </div>
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: '#64748b',
                    display: 'flex',
                    gap: '6px',
                    alignItems: 'center',
                  }}
                >
                  <TaskElapsed startedAt={task.startedAt} />
                  <span>· {STATUS_LABELS[task.status] || 'procesando'}</span>
                </div>
              </div>

              {/* Botón cancelar */}
              <button
                type="button"
                onClick={() => handleCancelTask(task)}
                disabled={cancellingIds.includes(task.taskId)}
                title="Cancelar esta transcripción"
                style={{
                  background: 'transparent',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#dc2626',
                  padding: '5px 8px',
                  cursor: cancellingIds.includes(task.taskId) ? 'wait' : 'pointer',
                  flexShrink: 0,
                }}
              >
                {cancellingIds.includes(task.taskId) ? (
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.8rem' }} />
                ) : (
                  <i className="pi pi-trash" style={{ fontSize: '0.8rem' }} />
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActiveTasksBadge;
